/* src/modules/directory/EmployeeDirectory.js */
import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot, where, getDocs } from 'firebase/firestore'; 
import { db } from '../../firebase/config';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { EditIcon, EyeIcon } from '../../components/icons/Icons';
import EmployeeProfileModal from './EmployeeProfileModal';
import AchievementCard from '../peer/AchievementCard'; 

// Helper for sort direction indicator
const SortIndicator = ({ direction }) => {
    if (!direction) return null;
    return direction === 'ascending' ? ' ▲' : ' ▼';
};

// ✅ HELPER: Format schedule as O-H-O-H-O
const getSchedulePattern = (userId, schedules) => {
    // 1. Find the active schedule for this user
    const today = new Date().toISOString().split('T')[0];
    const userSchedule = schedules.find(s => 
        s.assignedUsers.includes(userId) && 
        s.startDate <= today && 
        s.endDate >= today
    );

    if (!userSchedule) return null;

    const { pattern } = userSchedule;
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    
    // 2. Map days to single chars (O = Office, H = Home)
    const mapCode = (dayKey) => {
        const status = pattern[dayKey];
        if (status === 'WFO') return 'O';
        return 'H'; // Default to Home if not explicitly WFO
    };

    // 3. Join with hyphens
    return days.map(d => mapCode(d)).join('-');
};

export default function EmployeeDirectory({ appId, canManage, userProfile }) {
    const [users, setUsers] = useState([]);
    const [schedules, setSchedules] = useState([]); 
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [employeeStatusFilter, setEmployeeStatusFilter] = useState('Approved');
    const [cardUserId, setCardUserId] = useState(null);

    const [modalState, setModalState] = useState({
        isOpen: false,
        user: null,
        readOnly: false,
    });

    const canViewDetails = useMemo(() => {
        return userProfile?.role === 'Admin' || 
               userProfile?.department === 'HR';
    }, [userProfile]);

    useEffect(() => {
        setIsLoading(true);
        
        // A. Users Listener
        const usersCollectionRef = collection(db, `/artifacts/${appId}/users`);
        const qUsers = query(
            usersCollectionRef, 
            where("status", "==", employeeStatusFilter)
        );
        
        const unsubUsers = onSnapshot(qUsers, (snapshot) => {
            const usersData = snapshot.docs.map(d => ({ 
                id: d.id, ...d.data() 
            }));
            setUsers(usersData);
            setIsLoading(false);
        });

        // B. Fetch Schedules
        const fetchSchedules = async () => {
            try {
                const schedRef = collection(db, `artifacts/${appId}/office_schedules`);
                const snap = await getDocs(schedRef);
                const schedData = snap.docs.map(d => d.data());
                setSchedules(schedData);
            } catch (e) {
                console.error("Error fetching schedules:", e);
            }
        };
        fetchSchedules();

        return () => unsubUsers();
    }, [appId, employeeStatusFilter]);

    const handleOpenEditModal = (user) => {
        setModalState({ isOpen: true, user: user, readOnly: false });
    };

    const handleOpenViewModal = (user) => {
        setModalState({ isOpen: true, user: user, readOnly: true });
    };

    const handleCloseModal = () => {
        setModalState({ isOpen: false, user: null, readOnly: false });
    };
    
    const filteredUsers = useMemo(() => {
        if (!searchTerm) return users;
        const lowercasedTerm = searchTerm.toLowerCase();
        return users.filter(user =>
            user.name?.toLowerCase().includes(lowercasedTerm) ||
            user.department?.toLowerCase().includes(lowercasedTerm) ||
            user.designation?.toLowerCase().includes(lowercasedTerm) ||
            user.employeeId?.toLowerCase().includes(lowercasedTerm) ||
            user.location?.toLowerCase().includes(lowercasedTerm) ||
            user.email?.toLowerCase().includes(lowercasedTerm)
        );
    }, [users, searchTerm]);

    const userMap = useMemo(() => {
        return new Map(users.map(user => [user.id, user.name]));
    }, [users]);

    return (
        <div>
            <div className="flex justify-between items-center mb-4">
                 <input
                    type="text"
                    placeholder="Search directory..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full md:w-1/3 p-2 border border-gray-300 rounded-lg shadow-sm"
                />
                
                <div className="flex items-center space-x-4">
                    <select
                        value={employeeStatusFilter}
                        onChange={(e) => setEmployeeStatusFilter(e.target.value)}
                        className="p-2 border border-gray-300 rounded-lg shadow-sm bg-white"
                    >
                        <option value="Approved">Current Employees</option>
                        <option value="Deactivated">Past Employees</option>
                    </select>

                    <span className="text-sm text-gray-600 font-medium">
                        Showing {filteredUsers.length} records
                    </span>
                </div>
            </div>

            {isLoading ? <LoadingSpinner /> : (
                <UserList 
                    users={filteredUsers} 
                    userMap={userMap}
                    schedules={schedules} 
                    canManage={canManage}
                    canViewDetails={canViewDetails}
                    onEdit={handleOpenEditModal}
                    onView={handleOpenViewModal}
                    onViewCard={(id) => setCardUserId(id)} 
                />
            )}

            {modalState.isOpen && (
                <EmployeeProfileModal 
                    appId={appId}
                    allUsers={users}
                    existingUser={modalState.user}
                    onClose={handleCloseModal}
                    readOnly={modalState.readOnly}
                />
            )}

            {cardUserId && (
                <AchievementCard 
                    userId={cardUserId}
                    appId={appId}
                    onClose={() => setCardUserId(null)}
                />
            )}
        </div>
    );
}

function UserList({ 
    users, userMap, schedules, canManage, canViewDetails, onEdit, onView, onViewCard 
}) {
    const [sortConfig, setSortConfig] = useState({ 
        key: 'name', direction: 'ascending' 
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    useEffect(() => {
        setCurrentPage(1);
    }, [users]);

    const sortedUsers = useMemo(() => {
        let sortableItems = [...users];
        sortableItems.sort((a, b) => {
            if (sortConfig.key === 'manager') {
                const aManagerName = userMap.get(a.managerId) || '';
                const bManagerName = userMap.get(b.managerId) || '';
                if (aManagerName < bManagerName) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (aManagerName > bManagerName) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            }
            const aVal = (a[sortConfig.key] || '').toLowerCase();
            const bVal = (b[sortConfig.key] || '').toLowerCase();
            if (aVal < bVal) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
        });
        return sortableItems;
    }, [users, sortConfig, userMap]);
    
    const paginatedUsers = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return sortedUsers.slice(startIndex, endIndex);
    }, [sortedUsers, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(sortedUsers.length / itemsPerPage);

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) setCurrentPage(newPage);
    };
    
    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(Number(event.target.value));
        setCurrentPage(1);
    };

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
        setCurrentPage(1);
    };

    return (
        <>
            <div className="overflow-x-auto bg-white shadow-md rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Photo</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer" onClick={() => requestSort('name')}>
                                Name <SortIndicator direction={sortConfig.key === 'name' ? sortConfig.direction : null} />
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer" onClick={() => requestSort('department')}>
                                Department <SortIndicator direction={sortConfig.key === 'department' ? sortConfig.direction : null} />
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Schedule (M-F)</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer" onClick={() => requestSort('manager')}>
                                Manager <SortIndicator direction={sortConfig.key === 'manager' ? sortConfig.direction : null} />
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Designation</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                            { (canManage || canViewDetails) && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th> }
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {paginatedUsers.map((user) => {
                            // Calculate schedule string
                            const patternStr = getSchedulePattern(user.id, schedules);
                            
                            return (
                                <tr key={user.id}>
                                    <td className="px-6 py-4">
                                        <div 
                                            onClick={() => onViewCard(user.id)}
                                            className="cursor-pointer hover:scale-110 transition-transform duration-200 inline-block"
                                            title="Click to view Achievement Card"
                                        >
                                            <img 
                                                className="h-10 w-10 rounded-full object-cover border border-gray-200 shadow-sm" 
                                                src={user.photoURL && user.photoURL.length > 10 ? user.photoURL : `https://ui-avatars.com/api/?name=${(user.name || 'User').replace(/\s/g, '+')}&background=random`} 
                                                alt={user.name}
                                                referrerPolicy="no-referrer"
                                                onError={(e) => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${(user.name || 'User').replace(/\s/g, '+')}&background=random`; }}
                                            />
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 font-medium text-[#213368]">
                                        <div>{user.name}</div>
                                        <div className="text-xs text-gray-400 font-mono">{user.employeeId}</div>
                                    </td>
                                    <td className="px-6 py-4 text-sm">{user.department}</td>
                                    
                                    {/* ✅ UPDATED SCHEDULE COLUMN */}
                                    <td className="px-6 py-4 text-sm">
                                        {patternStr ? (
                                            <div className="flex items-center space-x-1 font-mono text-xs font-bold bg-gray-50 px-2 py-1 rounded border border-gray-100 w-fit">
                                                {/* We map the characters to styled spans for clarity */}
                                                {patternStr.split('-').map((char, idx) => (
                                                    <React.Fragment key={idx}>
                                                        <span className={char === 'O' ? 'text-blue-600' : 'text-gray-400'}>
                                                            {char}
                                                        </span>
                                                        {idx < 4 && <span className="text-gray-300">-</span>}
                                                    </React.Fragment>
                                                ))}
                                            </div>
                                        ) : (
                                            <span className="text-gray-400 text-xs italic">Not assigned</span>
                                        )}
                                    </td>

                                    <td className="px-6 py-4 text-sm">{userMap.get(user.managerId) || 'N/A'}</td>
                                    <td className="px-6 py-4 text-sm">{user.designation}</td>
                                    <td className="px-6 py-4 text-sm">{user.location}</td>
                                    <td className="px-6 py-4 text-sm">
                                        <div>{`${user.phoneCountryCode || ''} ${user.phoneNumber || ''}`}</div>
                                        <div className="text-gray-500 text-xs">{user.email}</div>
                                    </td>
                                    { (canManage || canViewDetails) &&
                                        <td className="px-6 py-4">
                                            <div className="flex items-center space-x-2">
                                                {canViewDetails && (
                                                    <button onClick={() => onView(user)} title="View Full Profile" className="p-2 text-blue-500 hover:text-blue-700"><EyeIcon /></button>
                                                )}
                                                {canManage && (
                                                    <button onClick={() => onEdit(user)} title="Edit Profile" className="p-2 text-yellow-500 hover:text-yellow-700"><EditIcon /></button>
                                                )}
                                            </div>
                                        </td>
                                    }
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {/* Pagination Controls */}
             {totalPages > 1 && (
                <div className="flex justify-between items-center mt-4">
                    <div className="flex items-center space-x-2 text-sm">
                        <span>Items per page:</span>
                        <select value={itemsPerPage} onChange={handleItemsPerPageChange} className="border border-gray-300 rounded-md p-1">
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                    <div className="flex items-center space-x-2">
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 border rounded-md disabled:opacity-50">
                            Previous
                        </button>
                        <span className="text-sm">
                            Page {currentPage} of {totalPages}
                        </span>
                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1 border rounded-md disabled:opacity-50">
                            Next
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}